import { action } from '@play-co/replicant';
import createActions from '../config/actions/createActions';
import { assertNever } from '../utils';
import {
  canFuse,
  canSell,
  getDuplicateItems,
  getCatalogItem,
} from './catalog.getters';
import {
  addDecorationItem,
  removeDecorationItemTier,
} from '../decoration/decoration.modifiers';
import {
  addAccessoryItem,
  removeAccessoryItemTier,
} from '../accessory/accessory.modifiers';
import * as Inventory from '../inventory';
import { Feature, sendAnalyticsEvents } from '../analytics';
import { CatalogItem } from './catalog.ruleset';

export const catalogActions = createActions({
  sellCatalogItem: action(
    (state, args: Pick<CatalogItem, 'id' | 'tierId' | 'type'>, api) => {
      // Check if user has item.
      const item = getCatalogItem(state, args.type, args.id, args.tierId);

      if (!canSell(state, item)) {
        throw new Error(`Cannot sell item '${item.id}'.`);
      }

      if (item.quantity === 0) {
        throw new Error(`Cannot sell item. Item '${item.id}' not owned.`);
      }

      // Remove item.
      const type = item.type;
      switch (type) {
        case 'decoration':
          removeDecorationItemTier(state, item.id, item.tierId);
          break;
        case 'accessory':
          removeAccessoryItemTier(state, item.id, item.tierId);
          break;
        default:
          assertNever(type);
      }

      const now = api.date.now();

      // Grant currency.
      Inventory.modifiers.addPoints(
        state,
        item.sellPrice,
        {
          feature: Feature.CATALOG.SELL,
          $subFeature: item.id,
        },
        api,
      );

      // Send analytics event.
      sendAnalyticsEvents(api, [
        {
          eventType: 'ItemSell',
          eventProperties: {
            itemTierID: item.tierId,
            tier: item.tier,
            itemID: item.id,
            numItems: getDuplicateItems(state, item.id, item.tierId),
            bPointsGranted: item.sellPrice,
            timestamp: new Date(now).toISOString(),
          },
        },
      ]);
    },
  ),

  fuseCatalogItem: action(
    (state, args: Pick<CatalogItem, 'id' | 'tierId' | 'type'>, api) => {
      // Check if user has item.
      const item = getCatalogItem(state, args.type, args.id, args.tierId);

      if (!canFuse(state, item)) {
        throw new Error(`Cannot fuse item '${item.id}'.`);
      }

      const now = api.date.now();

      // Take currencies.
      Inventory.modifiers.consumePoints(
        state,
        item.fusePrice,
        {
          feature: Feature.CATALOG.FUSION,
          $subFeature: item.id,
        },
        api,
      );

      // Remove items.
      const type = item.type;
      switch (type) {
        case 'decoration':
          removeDecorationItemTier(
            state,
            item.id,
            item.tierId,
            item.fuseItemsRequired,
          );
          break;

        case 'accessory':
          removeAccessoryItemTier(
            state,
            item.id,
            item.tierId,
            item.fuseItemsRequired,
          );
          break;
        default:
          assertNever(type);
      }

      // Add next tier.
      switch (type) {
        case 'decoration':
          addDecorationItem(state, item.id, item.nextTierId, now);
          break;
        case 'accessory':
          addAccessoryItem(state, item.id, item.nextTierId, now);
          break;
        default:
          assertNever(type);
      }

      // Get new item.
      const newItem = getCatalogItem(
        state,
        item.type,
        item.id,
        item.nextTierId,
      );

      // Send analytics event.
      sendAnalyticsEvents(api, [
        {
          eventType: 'ItemFusion',
          eventProperties: {
            itemID: item.id,
            oldItemTierID: item.tierId,
            oldTier: item.tier,
            newItemTierID: newItem.tierId,
            newTier: newItem.tier,
            numItems: getDuplicateItems(state, item.id, item.tierId),
            bPointsConsumed: item.fusePrice,
            numItemNeeded: item.fuseItemsRequired,
            timestamp: new Date(now).toISOString(),
          },
        },
      ]);
    },
  ),
});
